import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { REFERENCES, REFERENCE_ORDER } from './references';
import { CHAPTER_BAR, HEADER, usePlayOnView } from './motion';

// Reference frames threaded on a 3D helix, flying out of the distance toward
// the viewer, while three lines hand over in the middle. BYQ Supply's "Helix
// Corridor" gem: the helix's geometry and every constant are theirs, kept as
// written. Ours: it plays by itself, once, when the panel is mostly on screen,
// instead of being driven by a long scroll; it is one screen tall; it rests on
// its last line; and the colours and type are the site's.

// ─── Corridor constants (BYQ's, unchanged) ──────────────────────────────────
const PERSPECTIVE = 1150;
const S_FAR = 0.16;
const S_NEAR = 2.6;
const S_CURVE = 2.4;
const TURNS = 1;
const R_FAR = 0.2;
const R_CURVE = 0.62;
const SQUASH = 0.55;
const RISE = 0.84;
const DRIFT = 0.42;
const BANK = 9;
const SPLIT_Z = 450;
const LOOPS = 1;
const TAU = Math.PI * 2;

// One pass of the corridor, and the lines with it. Eased in and out, so the
// frames gather speed from the distance and settle at the end.
const DURATION = 9000;
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// The fifteen frames, twice round
const TILES = [0, 1].flatMap(() => REFERENCE_ORDER.map((name) => REFERENCES[name]));
const N = TILES.length;
const STEP = 1 / N;

const LINES = [
  'Every frame you’ve fallen for.',
  'From Frameset, or any website.',
  'Dragged straight into a shot list.',
];

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
function ramp(a, b, v) {
  const t = clamp((v - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}
function noise(i, salt) {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}
const LOOK = TILES.map((_, i) => ({
  rx: -6 + noise(i, 1) * 14,
  ry: -38 + noise(i, 2) * 24,
  rz: -6 + noise(i, 3) * 12,
  scale: 0.86 + noise(i, 4) * 0.3,
}));

// `covered` is the window's top that the header and pinned bars hide
export function HelixCorridor({ covered = HEADER + CHAPTER_BAR }) {
  const panelRef = useRef(null);
  const stageRef = useRef(null);
  const frontRef = useRef(null);
  const tileRefs = useRef([]);
  const lineRefs = useRef([]);
  const playRef = useRef(null);
  const resetRef = useRef(null);
  const [ready, setReady] = useState(false);
  // Plays each time most of the panel comes into view, and resets once it has gone
  const inView = usePlayOnView(panelRef, 0.6);
  useEffect(() => {
    if (!ready) return;
    if (inView) playRef.current?.();
    else resetRef.current?.();
  }, [inView, ready]);

  useEffect(() => {
    const root = panelRef.current;
    const stage = stageRef.current;
    const front = frontRef.current;
    if (!root || !stage || !front) return undefined;
    const tiles = tileRefs.current.filter(Boolean);
    const lines = lineRefs.current.filter(Boolean);
    if (!tiles.length) return undefined;

    let radius = 0;
    let viewH = 0;
    let viewW = 0;
    let unit = 1;
    let travel = 0;
    let progress = 0;
    let rafId = 0;
    const inFront = tiles.map(() => false);

    function measure() {
      const w = root.clientWidth;
      const h = root.clientHeight;
      viewH = h;
      viewW = w;
      radius = clamp(Math.min(w * 0.3, h * 0.46), 96, 520);
      unit = clamp(Math.min(w, h) / 880, 0.4, 1);
    }

    function render() {
      for (let i = 0; i < N; i += 1) {
        let ph = (i * STEP + travel) % 1;
        if (ph < 0) ph += 1;
        const s = S_FAR + (S_NEAR - S_FAR) * Math.pow(ph, S_CURVE);
        const z = PERSPECTIVE * (1 - 1 / s);
        const angle = ph * TURNS * TAU;
        const r = radius * (R_FAR + (1 - R_FAR) * Math.pow(ph, R_CURVE));
        const lean = (0.5 - ph) / s;
        const x = Math.cos(angle) * r + lean * DRIFT * viewW;
        const y = Math.sin(angle) * r * SQUASH + lean * RISE * viewH;
        const l = LOOK[i];
        const roll = l.rz + BANK * Math.cos(angle);
        const tile = tiles[i];
        const wantFront = z > SPLIT_Z;
        if (wantFront !== inFront[i]) {
          inFront[i] = wantFront;
          (wantFront ? front : stage).appendChild(tile);
        }
        tile.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,${z.toFixed(2)}px) rotateX(${l.rx.toFixed(2)}deg) rotateY(${l.ry.toFixed(2)}deg) rotateZ(${roll.toFixed(2)}deg) scale(${(l.scale * unit).toFixed(3)})`;
        tile.style.opacity = (ramp(0, 0.1, ph) * (1 - ramp(0.8, 1, ph))).toFixed(3);
      }
    }

    function paintLines() {
      if (!lines.length) return;
      const span = 1 / lines.length;
      for (let i = 0; i < lines.length; i += 1) {
        const centre = (i + 0.5) * span;
        const offset = progress - centre;
        const away = Math.abs(offset) / span;
        lines[i].style.opacity = (1 - ramp(0.12, 0.62, away)).toFixed(3);
        lines[i].style.transform = `translateY(${(-50 - offset * 260).toFixed(2)}%)`;
      }
    }

    // One pass: the helix travels once round, and the lines run from the
    // first one, centred, to the last, which stays
    const span = 1 / Math.max(lines.length, 1);
    function frame(t) {
      travel = easeInOut(t) * LOOPS;
      progress = span / 2 + t * (1 - span);
      render();
      paintLines();
    }

    function play() {
      window.cancelAnimationFrame(rafId);
      const start = window.performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / DURATION);
        frame(t);
        if (t < 1) rafId = window.requestAnimationFrame(tick);
      };
      rafId = window.requestAnimationFrame(tick);
    }
    playRef.current = play;
    // Back to the start while out of view, so a return plays from the top
    resetRef.current = () => {
      window.cancelAnimationFrame(rafId);
      frame(0);
    };

    measure();
    // Reduced motion: one still frame with the last line, and nothing moves
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      travel = 0.4;
      progress = 1 - span / 2;
      render();
      paintLines();
      return undefined;
    }
    frame(0);
    setReady(true);

    const onResize = () => {
      measure();
      render();
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      window.cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    // One screen, less the header, so it fills the window when it pins under it
    <div className="relative w-full">
      <div
        ref={panelRef}
        className="relative w-full overflow-hidden"
        style={{ height: `calc(100dvh - ${covered}px)`, minHeight: 480, perspective: `${PERSPECTIVE}px`, perspectiveOrigin: '50% 50%' }}
      >
        {/* Tiles behind the words */}
        <div ref={stageRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 1, transformStyle: 'preserve-3d' }}>
          {TILES.map((tile, i) => (
            <figure
              key={i}
              ref={(el) => { tileRefs.current[i] = el; }}
              className={`helix-tile helix-tile--${tile.shape}`}
            >
              <img className="helix-img" src={tile.src} width={tile.w} height={tile.h} alt="" decoding="async" draggable={false} />
            </figure>
          ))}
        </div>

        {/* The words, flat, between the two stages */}
        <div
          className="absolute pointer-events-none"
          style={{ left: '50%', top: '50%', width: 'min(90%, 54rem)', height: '12rem', zIndex: 2, transform: 'translate(-50%, -50%)' }}
        >
          {LINES.map((text, i) => (
            <p key={text} ref={(el) => { lineRefs.current[i] = el; }} className="helix-line">
              {text}
            </p>
          ))}
        </div>

        {/* Tiles in front of the words */}
        <div ref={frontRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 3, transformStyle: 'preserve-3d' }} />

        {/* What this is, and that it is coming */}
        <div className="absolute inset-x-0 bottom-0 z-[4] pb-8 flex flex-col items-center gap-2 text-center pointer-events-none">
          <p className="text-lg">Shot lists</p>
          <p className="rounded-pill bg-ochre-soft text-foreground px-3 py-1 text-sm">Filmmaker plan, coming soon</p>
        </div>
      </div>
    </div>
  );
}
